import { Item } from '@/api/items/items.types';
import {
  Box,
  Card,
  CardContent,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { AddShoppingCart } from '@mui/icons-material';
import { useUpsertCartItem } from '@/api/carts/carts.hooks';
import { formatMoneyString } from '@utils/format-money-string';
import { useTranslation } from 'react-i18next';

const PLACEHOLDER_SVG = `data:image/svg+xml,${encodeURIComponent(`<svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="120" height="120" rx="8" fill="#f0f0f0"/><path d="M36 78l15-20 10.5 13.5 15-19.5L93 78" stroke="#c0c0c0" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/><circle cx="48" cy="48" r="6" fill="#c0c0c0"/></svg>`)}`;

const STOCK_COLORS = {
  available: '#43a047',
  ordered: '#ef6c00',
  unavailable: '#e53935',
} as const;

export function ProductCard({ item }: { item: Item }) {
  const { t } = useTranslation('customer');
  const addToCart = useUpsertCartItem();

  // Stock computation
  const availableQuantity = Math.max(
    item.quantityOnStock - item.quantityOrderedByCustomers,
    0,
  );
  const orderedQuantity = item.quantityOrderedFromVendors;

  let stockColor: string;
  let stockLabel: string;
  let stockTooltip: string;

  if (availableQuantity === 0 && orderedQuantity === 0) {
    stockColor = STOCK_COLORS.unavailable;
    stockLabel = t('items.stock.notAvailable');
    stockTooltip = t('items.stock.notAvailableTooltip');
  } else if (availableQuantity === 0 && orderedQuantity > 0) {
    stockColor = STOCK_COLORS.ordered;
    stockLabel = t('items.stock.ordered');
    stockTooltip = t('items.stock.deliveryDate');
  } else {
    stockColor = STOCK_COLORS.available;
    const getLabel = (qty: number): string => {
      if (qty < 20) return `${qty}`;
      if (qty < 50) return '> 20';
      if (qty < 100) return '> 50';
      if (qty < 200) return '> 100';
      if (qty < 500) return '> 200';
      return '> 500';
    };
    stockLabel = getLabel(availableQuantity);
    stockTooltip =
      orderedQuantity > 0
        ? `${stockLabel}, ${t('items.stock.additionalOrdered', { count: orderedQuantity, date: 'N/A' })}`
        : stockLabel;
  }

  return (
    <Card
      variant="outlined"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        borderLeftWidth: 2,
        borderLeftColor: `${stockColor}55`,
        transition: 'box-shadow 0.2s ease, transform 0.2s ease',
        '&:hover': {
          boxShadow: '0 4px 20px rgba(2, 52, 76, 0.10)',
          transform: 'translateY(-1px)',
        },
      }}
    >
      {/* Image */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 144,
          bgcolor: '#fff',
          px: 0.5,
          py: 0.5,
        }}
      >
        <img
          src={item.imageUrl || PLACEHOLDER_SVG}
          alt=""
          loading="lazy"
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
          }}
        />
      </Box>

      {/* Content */}
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          p: 1.5,
          '&:last-child': { pb: 1.5 },
        }}
      >
        {/* Name */}
        <Tooltip title={item.name} enterDelay={400}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontSize: '0.8rem',
              lineHeight: 1.35,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              minHeight: '2.2em',
              mb: 0.25,
            }}
          >
            {item.name}
          </Typography>
        </Tooltip>

        {/* Article Number */}
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{
            fontFamily: '"Roboto Mono", monospace',
            fontSize: '0.65rem',
            letterSpacing: '0.02em',
            mb: 0.75,
          }}
        >
          {item.id}
        </Typography>

        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Stock Indicator */}
        <Tooltip title={stockTooltip} arrow>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              mb: 1,
              cursor: 'default',
            }}
          >
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                bgcolor: stockColor,
                flexShrink: 0,
                boxShadow: `0 0 0 2px ${stockColor}22`,
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.68rem',
                color: 'text.secondary',
                fontWeight: 500,
              }}
            >
              {stockLabel}
            </Typography>
          </Box>
        </Tooltip>

        <Divider sx={{ mb: 1 }} />

        {/* Price + Cart */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              fontSize: '0.95rem',
              color: 'primary.main',
              letterSpacing: '-0.01em',
            }}
          >
            {item.itemPrice > 0
              ? formatMoneyString(item.itemPrice)
              : 'Auf Anfrage'}
          </Typography>
          <Tooltip title="In den Warenkorb">
            <IconButton
              size="small"
              onClick={() =>
                addToCart.mutate({
                  cartItems: [{ itemId: item.id, quantity: 1 }],
                })
              }
              sx={{
                color: 'primary.main',
                bgcolor: 'rgba(2, 52, 76, 0.06)',
                width: 30,
                height: 30,
                transition: 'all 0.15s ease',
                '&:hover': {
                  bgcolor: 'primary.main',
                  color: 'white',
                },
              }}
            >
              <AddShoppingCart sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </CardContent>
    </Card>
  );
}
